import html2pdf from 'html2pdf.js';

const socialBases = {
  linkedin: 'https://www.linkedin.com/in/',
  x: 'https://x.com/',
  twitter: 'https://x.com/',
  github: 'https://github.com/',
  youtube: 'https://www.youtube.com/'
};

const socialLabels = {
  linkedin: 'LinkedIn',
  x: 'X',
  twitter: 'X',
  github: 'GitHub',
  youtube: 'YouTube'
};

const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const cleanUrl = (value = '') => String(value).trim();

const getFileName = (name, extension) => {
  const safeName = String(name || 'resume')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return `${safeName || 'resume'}_resume.${extension}`;
};

const getFont = (fontFamily) => {
  const font = String(fontFamily || 'Inter').replace(/[^a-zA-Z0-9 ]/g, '');
  return `"${font}", "Segoe UI", Arial, sans-serif`;
};

const getPdfBackgroundColor = (background) => {
  const value = String(background || '').trim();
  return /^#[0-9a-f]{3,8}$/i.test(value) ? value : '#111111';
};

const getSocialIcon = (platform) => {
  const icons = {
    linkedin: 'in',
    x: '𝕏',
    twitter: '𝕏',
    github: '⌘',
    youtube: '▶'
  };

  return icons[platform] || '↗';
};

export const normalizeWebsiteUrl = (value) => {
  const url = cleanUrl(value);

  if (!url) return '';
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  if (url.startsWith('//')) return `https:${url}`;

  return `https://${url.replace(/^\/+/, '')}`;
};

export const normalizeSocialUrl = (platform, value) => {
  const rawValue = cleanUrl(value);

  if (!rawValue) return '';
  if (/^https?:\/\//i.test(rawValue)) return rawValue;
  if (rawValue.startsWith('//')) return `https:${rawValue}`;

  const cleanValue = rawValue
    .replace(/^@+/, '')
    .replace(/^\/+/, '');

  if (/^(www\.)?[a-z0-9-]+\.[a-z]{2,}(\/|$)/i.test(cleanValue)) {
    return `https://${cleanValue}`;
  }

  const username = cleanValue
    .replace(
      /^(www\.)?(linkedin\.com\/in\/|github\.com\/|x\.com\/|twitter\.com\/|youtube\.com\/(c\/|@)?)/i,
      ''
    )
    .replace(/^@+/, '');

  return `${socialBases[platform] || 'https://'}${username}`;
};

const renderSocialLinks = (socials = {}, mode = 'html') => Object.entries(socials)
  .filter(([, value]) => cleanUrl(value))
  .map(([platform, value]) => {
    const label = socialLabels[platform] || platform;
    const url = normalizeSocialUrl(platform, value);

    if (mode === 'pdf') {
      return `
        <a class="social-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">
          <span class="social-icon">${escapeHtml(getSocialIcon(platform))}</span>
          <span>${escapeHtml(label)}</span>
        </a>
      `;
    }

    return `
      <a class="social-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">
        ${escapeHtml(label)}
      </a>
    `;
  })
  .join('');

const renderExperience = (items = []) => items
  .filter((item) => item.role || item.company || item.year || item.desc)
  .map((item) => `
    <article class="experience-item">
      <div class="experience-date">${escapeHtml(item.year)}</div>
      <div class="experience-content">
        <h4>${escapeHtml(item.role)}</h4>
        <strong>${escapeHtml(item.company)}</strong>
        ${item.desc ? `<p>${escapeHtml(item.desc)}</p>` : ''}
      </div>
    </article>
  `)
  .join('');

const renderEducation = (items = []) => items
  .filter((item) => item.degree || item.school || item.year)
  .map((item) => `
    <article class="education-item">
      <span class="education-year">${escapeHtml(item.year)}</span>
      <h4>${escapeHtml(item.degree)}</h4>
      <p>${escapeHtml(item.school)}</p>
    </article>
  `)
  .join('');

const renderTags = (items = []) => items
  .filter(Boolean)
  .map((item) => `<span class="tag">${escapeHtml(item)}</span>`)
  .join('');

const renderReferences = (items = []) => items
  .filter((item) => item.name || item.role || item.company || item.contact)
  .map((item) => `
    <article class="reference-item">
      <h4>${escapeHtml(item.name)}</h4>
      <p>${escapeHtml(item.role)}${item.company ? ` · ${escapeHtml(item.company)}` : ''}</p>
      <span>${escapeHtml(item.contact)}</span>
    </article>
  `)
  .join('');

const renderContactRows = (bioData) => {
  const websiteUrl = normalizeWebsiteUrl(bioData.website);

  return `
    ${bioData.email ? `
      <a class="contact-row" href="mailto:${escapeHtml(bioData.email)}">
        <span>✉</span><span>${escapeHtml(bioData.email)}</span>
      </a>
    ` : ''}
    ${bioData.phone ? `
      <a class="contact-row" href="tel:${escapeHtml(bioData.phone)}">
        <span>☎</span><span>${escapeHtml(bioData.phone)}</span>
      </a>
    ` : ''}
    ${bioData.location ? `
      <div class="contact-row">
        <span>●</span><span>${escapeHtml(bioData.location)}</span>
      </div>
    ` : ''}
    ${bioData.website ? `
      <a class="contact-row" href="${escapeHtml(websiteUrl)}" target="_blank" rel="noopener noreferrer">
        <span>↗</span><span>${escapeHtml(bioData.website)}</span>
      </a>
    ` : ''}
  `;
};

const section = (title, body, className = '') => `
  <section class="resume-section ${className}">
    <h3>${escapeHtml(title)}</h3>
    ${body}
  </section>
`;

export const generateResumeHtml = (bioData) => {
  const colors = bioData.colors || {};
  const cardStyle = bioData.cardStyle || {};
  const avatar = bioData.avatar
    ? `<img src="${escapeHtml(bioData.avatar)}" alt="${escapeHtml(bioData.name)}">`
    : `<span>${escapeHtml(bioData.name?.charAt(0) || 'R')}</span>`;

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(bioData.name)} - Resume</title>
  <style>
    :root {
      --page: ${colors.background || '#111111'};
      --card: ${colors.cardBackground || '#1e1e1e'};
      --border: ${colors.cardBorder || '#333333'};
      --text: ${colors.text || '#ffffff'};
      --accent: ${colors.accent || '#a1a1aa'};
      --radius: ${cardStyle.borderRadius || '16px'};
      --shadow: ${cardStyle.shadow || 'none'};
    }

    * {
      box-sizing: border-box;
    }

    body {
      min-height: 100vh;
      margin: 0;
      padding: 28px;
      color: var(--text);
      background: var(--page);
      font-family: ${getFont(bioData.fontFamily)};
      line-height: 1.5;
    }

    .resume {
      width: min(100%, 1000px);
      margin: 0 auto;
    }

    .resume-header,
    .resume-section {
      background: var(--card);
      border: ${cardStyle.borderWidth || '1px'} solid var(--border);
      border-radius: var(--radius);
      box-shadow: var(--shadow);
    }

    .resume-header {
      display: flex;
      gap: 28px;
      align-items: center;
      padding: 30px;
      margin-bottom: 18px;
    }

    .avatar {
      display: grid;
      width: 120px;
      height: 120px;
      flex: 0 0 120px;
      place-items: center;
      overflow: hidden;
      border: 3px solid var(--accent);
      border-radius: 50%;
      background: rgba(255, 255, 255, .08);
      font-size: 42px;
      font-weight: 800;
    }

    .avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    h1 {
      margin: 0;
      font-size: 36px;
      line-height: 1.1;
    }

    .role {
      margin: 7px 0 16px;
      color: var(--accent);
      font-size: 17px;
      font-weight: 700;
    }

    .socials,
    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .social-link,
    .tag {
      display: inline-flex;
      align-items: center;
      padding: 7px 11px;
      border: 1px solid var(--border);
      border-radius: 999px;
      color: inherit;
      background: rgba(255, 255, 255, .05);
      font-size: 12px;
      font-weight: 700;
      text-decoration: none;
    }

    .resume-grid {
      display: grid;
      grid-template-columns: minmax(0, 1.15fr) minmax(260px, .85fr);
      gap: 18px;
      align-items: start;
    }

    .resume-column {
      display: grid;
      gap: 18px;
    }

    .resume-section {
      padding: 24px;
    }

    .resume-section h3 {
      margin: 0 0 17px;
      padding-bottom: 9px;
      border-bottom: 1px solid var(--border);
      color: var(--accent);
      font-size: 11px;
      letter-spacing: 1.6px;
      text-transform: uppercase;
    }

    .resume-section p {
      margin: 0;
      font-size: 13px;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    .contact-list {
      display: grid;
      gap: 11px;
    }

    .contact-row {
      display: grid;
      grid-template-columns: 18px minmax(0, 1fr);
      gap: 8px;
      color: inherit;
      font-size: 12px;
      text-decoration: none;
      overflow-wrap: anywhere;
    }

    .experience-item {
      position: relative;
      display: grid;
      grid-template-columns: 105px minmax(0, 1fr);
      gap: 12px;
      padding: 0 0 21px 16px;
      border-left: 2px solid var(--accent);
    }

    .experience-item:last-child {
      padding-bottom: 0;
    }

    .experience-item::before {
      position: absolute;
      top: 4px;
      left: -5px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--accent);
      content: "";
    }

    .experience-date,
    .education-year {
      color: var(--accent);
      font-size: 10px;
      font-weight: 800;
      letter-spacing: .6px;
      text-transform: uppercase;
    }

    h4 {
      margin: 0 0 3px;
      font-size: 14px;
    }

    .experience-content strong {
      display: block;
      margin-bottom: 7px;
      opacity: .75;
      font-size: 12px;
    }

    .education-item,
    .reference-item {
      margin-bottom: 12px;
      padding: 12px;
      border-radius: 10px;
      background: rgba(255, 255, 255, .05);
    }

    .education-item:last-child,
    .reference-item:last-child {
      margin-bottom: 0;
    }

    .education-item h4,
    .reference-item h4 {
      margin-top: 3px;
    }

    .education-item p,
    .reference-item p,
    .reference-item span {
      opacity: .72;
      font-size: 11px;
    }

    @media (max-width: 720px) {
      body {
        padding: 14px;
      }

      .resume-header {
        display: grid;
        justify-items: center;
        text-align: center;
      }

      .socials {
        justify-content: center;
      }

      .resume-grid {
        grid-template-columns: 1fr;
      }
    }
  </style>
</head>
<body>
  <main class="resume">
    <header class="resume-header">
      <div class="avatar">${avatar}</div>
      <div>
        <h1>${escapeHtml(bioData.name)}</h1>
        <p class="role">${escapeHtml(bioData.title)}</p>
        <nav class="socials">${renderSocialLinks(bioData.socials)}</nav>
      </div>
    </header>

    <div class="resume-grid">
      <div class="resume-column">
        ${section('Profile', `<p>${escapeHtml(bioData.about)}</p>`)}
        ${bioData.experience?.length ? section('Experience', renderExperience(bioData.experience)) : ''}
        ${bioData.references?.length ? section('References', renderReferences(bioData.references)) : ''}
      </div>

      <aside class="resume-column">
        ${section('Contact', `<div class="contact-list">${renderContactRows(bioData)}</div>`)}
        ${bioData.education?.length ? section('Education', renderEducation(bioData.education)) : ''}
        ${bioData.skills?.length ? section('Expertise', `<div class="tag-list">${renderTags(bioData.skills)}</div>`) : ''}
        ${bioData.languages?.length ? section('Languages', `<div class="tag-list">${renderTags(bioData.languages)}</div>`) : ''}
      </aside>
    </div>
  </main>
</body>
</html>`;
};

const generatePdfMarkup = (bioData) => {
  const colors = bioData.colors || {};
  const cardStyle = bioData.cardStyle || {};
  const pageBackground = getPdfBackgroundColor(colors.background);
  const avatar = bioData.avatar
    ? `<img src="${escapeHtml(bioData.avatar)}" alt="">`
    : `<span>${escapeHtml(bioData.name?.charAt(0) || 'R')}</span>`;

  return `
    <style>
      .pdf-export-root,
      .pdf-export-root * {
        box-sizing: border-box;
      }

      .pdf-export-root {
        width: 794px;
        min-height: 1123px;
        margin: 0;
        padding: 34px;
        color: ${colors.text || '#ffffff'};
        background: ${pageBackground};
        font-family: ${getFont(bioData.fontFamily)};
        line-height: 1.45;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .pdf-export-root .pdf-header {
        display: flex;
        align-items: center;
        gap: 22px;
        margin-bottom: 16px;
        padding: 22px;
        overflow: hidden;
        background: ${colors.cardBackground || '#1e1e1e'};
        border: ${cardStyle.borderWidth || '1px'} solid ${colors.cardBorder || '#333333'};
        border-radius: ${cardStyle.borderRadius || '16px'};
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .pdf-export-root .pdf-avatar {
        display: grid;
        width: 92px;
        height: 92px;
        flex: 0 0 92px;
        place-items: center;
        overflow: hidden;
        border: 3px solid ${colors.accent || '#a1a1aa'};
        border-radius: 50%;
        background: rgba(255, 255, 255, .08);
        color: ${colors.text || '#ffffff'};
        font-size: 31px;
        font-weight: 800;
      }

      .pdf-export-root .pdf-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .pdf-export-root h1 {
        margin: 0;
        font-size: 29px;
        letter-spacing: -.6px;
        line-height: 1.1;
      }

      .pdf-export-root .job-title {
        margin: 5px 0 12px;
        color: ${colors.accent || '#a1a1aa'};
        font-size: 14px;
        font-weight: 800;
      }

      .pdf-export-root .social-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .pdf-export-root .social-link {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 8px;
        border: 1px solid ${colors.cardBorder || '#333333'};
        border-radius: 999px;
        color: ${colors.text || '#ffffff'};
        background: rgba(255, 255, 255, .05);
        font-size: 10px;
        font-weight: 700;
        text-decoration: none;
      }

      .pdf-export-root .social-icon {
        color: ${colors.accent || '#a1a1aa'};
        font-size: 11px;
        line-height: 1;
      }

      .pdf-export-root .pdf-grid {
        display: grid;
        grid-template-columns: 1.18fr .82fr;
        gap: 16px;
        align-items: start;
      }

      .pdf-export-root .pdf-column {
        min-width: 0;
      }

      .pdf-export-root .pdf-section {
        margin-bottom: 16px;
        padding: 19px;
        overflow: hidden;
        background: ${colors.cardBackground || '#1e1e1e'};
        border: ${cardStyle.borderWidth || '1px'} solid ${colors.cardBorder || '#333333'};
        border-radius: ${cardStyle.borderRadius || '16px'};
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .pdf-export-root .pdf-section:last-child {
        margin-bottom: 0;
      }

      .pdf-export-root .pdf-section h3 {
        margin: 0 0 13px;
        padding-bottom: 7px;
        border-bottom: 1px solid ${colors.cardBorder || '#333333'};
        color: ${colors.accent || '#a1a1aa'};
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 1.45px;
        text-transform: uppercase;
      }

      .pdf-export-root .summary {
        margin: 0;
        font-size: 11px;
        line-height: 1.65;
        white-space: pre-wrap;
      }

      .pdf-export-root .contact-list {
        display: grid;
        gap: 8px;
      }

      .pdf-export-root .contact-row {
        display: grid;
        grid-template-columns: 15px minmax(0, 1fr);
        gap: 6px;
        color: ${colors.text || '#ffffff'};
        font-size: 10px;
        line-height: 1.35;
        text-decoration: none;
        overflow-wrap: anywhere;
      }

      .pdf-export-root .contact-row span:first-child {
        color: ${colors.accent || '#a1a1aa'};
      }

      .pdf-export-root .experience-item {
        position: relative;
        display: grid;
        grid-template-columns: 88px minmax(0, 1fr);
        gap: 10px;
        margin-left: 4px;
        padding: 0 0 17px 12px;
        border-left: 2px solid ${colors.accent || '#a1a1aa'};
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .pdf-export-root .experience-item:last-child {
        padding-bottom: 0;
      }

      .pdf-export-root .experience-item::before {
        position: absolute;
        top: 4px;
        left: -5px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${colors.accent || '#a1a1aa'};
        content: "";
      }

      .pdf-export-root .experience-date,
      .pdf-export-root .education-year {
        color: ${colors.accent || '#a1a1aa'};
        font-size: 9px;
        font-weight: 800;
        letter-spacing: .45px;
        text-transform: uppercase;
      }

      .pdf-export-root h4 {
        margin: 0 0 2px;
        font-size: 12px;
        line-height: 1.3;
      }

      .pdf-export-root .experience-content strong {
        display: block;
        margin-bottom: 5px;
        opacity: .75;
        font-size: 10px;
      }

      .pdf-export-root .experience-content p,
      .pdf-export-root .education-item p,
      .pdf-export-root .reference-item p,
      .pdf-export-root .reference-item span {
        margin: 0;
        opacity: .78;
        font-size: 10px;
        line-height: 1.45;
        overflow-wrap: anywhere;
      }

      .pdf-export-root .education-item,
      .pdf-export-root .reference-item {
        margin-bottom: 9px;
        padding: 10px;
        border-radius: 9px;
        background: rgba(255, 255, 255, .05);
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .pdf-export-root .education-item:last-child,
      .pdf-export-root .reference-item:last-child {
        margin-bottom: 0;
      }

      .pdf-export-root .education-item h4,
      .pdf-export-root .reference-item h4 {
        margin-top: 3px;
      }

      .pdf-export-root .tag-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .pdf-export-root .tag {
        display: inline-block;
        padding: 4px 8px;
        border: 1px solid ${colors.cardBorder || '#333333'};
        border-radius: 999px;
        background: rgba(255, 255, 255, .05);
        color: ${colors.text || '#ffffff'};
        font-size: 9px;
        font-weight: 700;
        line-height: 1.25;
      }
    </style>

    <main class="pdf-export-root">
      <header class="pdf-header">
        <div class="pdf-avatar">${avatar}</div>
        <div>
          <h1>${escapeHtml(bioData.name)}</h1>
          <p class="job-title">${escapeHtml(bioData.title)}</p>
          <nav class="social-list">${renderSocialLinks(bioData.socials, 'pdf')}</nav>
        </div>
      </header>

      <div class="pdf-grid">
        <div class="pdf-column">
          ${section('Profile', `<p class="summary">${escapeHtml(bioData.about)}</p>`, 'pdf-section')}
          ${bioData.experience?.length
            ? section('Experience', renderExperience(bioData.experience), 'pdf-section')
            : ''}
          ${bioData.references?.length
            ? section('References', renderReferences(bioData.references), 'pdf-section')
            : ''}
        </div>

        <aside class="pdf-column">
          ${section('Contact', `<div class="contact-list">${renderContactRows(bioData)}</div>`, 'pdf-section')}
          ${bioData.education?.length
            ? section('Education', renderEducation(bioData.education), 'pdf-section')
            : ''}
          ${bioData.skills?.length
            ? section('Expertise', `<div class="tag-list">${renderTags(bioData.skills)}</div>`, 'pdf-section')
            : ''}
          ${bioData.languages?.length
            ? section('Languages', `<div class="tag-list">${renderTags(bioData.languages)}</div>`, 'pdf-section')
            : ''}
        </aside>
      </div>
    </main>
  `;
};

export const downloadHtmlResume = (bioData) => {
  const blob = new Blob([generateResumeHtml(bioData)], {
    type: 'text/html;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = getFileName(bioData.name, 'html');
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const waitForExportAssets = async (container) => {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const images = Array.from(container.images);

  await Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve();

    return new Promise((resolve) => {
      const timeout = window.setTimeout(resolve, 3000);

      image.onload = () => {
        window.clearTimeout(timeout);
        resolve();
      };

      image.onerror = () => {
        window.clearTimeout(timeout);
        resolve();
      };
    });
  }));

  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
};

export const downloadPdfResume = async (bioData) => {
  const exportHost = document.createElement('div');

  /*
   * html2canvas does not render elements inside a visibility:hidden iframe.
   * The former PDF implementation used that pattern, producing blank or
   * incomplete pages. This rendered, in-document host remains invisible behind
   * the application while keeping every layout dimension available to canvas.
   */
  exportHost.setAttribute('aria-hidden', 'true');
  exportHost.style.cssText = [
    'position:absolute',
    'top:0',
    'left:0',
    'width:794px',
    'min-height:1123px',
    'z-index:-9999',
    'pointer-events:none',
    'overflow:visible'
  ].join(';');

  exportHost.innerHTML = generatePdfMarkup(bioData);
  document.body.appendChild(exportHost);

  try {
    const resume = exportHost.querySelector('.pdf-export-root');

    if (!resume) {
      throw new Error('Unable to prepare the resume for PDF export.');
    }

    await waitForExportAssets(exportHost);

    await html2pdf()
      .set({
        margin: 0,
        filename: getFileName(bioData.name, 'pdf'),
        image: {
          type: 'jpeg',
          quality: 0.98
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: getPdfBackgroundColor(bioData.colors?.background),
          windowWidth: 794,
          windowHeight: Math.max(1123, resume.scrollHeight),
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true
        },
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['.pdf-header', '.pdf-section', '.experience-item']
        }
      })
      .from(resume)
      .save();
  } finally {
    exportHost.remove();
  }
};